import { components } from "@/lib/ApiSchema";
import Button from "@/lib/Button";
import Input from "@/lib/Input";
import useGetTeamSettings from "@/lib/queries/useGetTeamSettings";
import useUpdateTeamSettings from "@/lib/queries/useUpdateTeamSettings";
import useUploadFiles from "@/lib/queries/useUploadFiles";
import SettingsSectionLayout from "@/lib/SettingsSectionLayout";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";

export default function QuoteTemplate() {
  const [input, setInput] = useState<
    components["schemas"]["TeamUpdateSchemaPatch"] | undefined
  >();
  const [logo, setLogo] = useState<File | null>(null);

  const { teamSlug } = useParams();
  const teamSettings = useGetTeamSettings(teamSlug);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    setInput({
      quote_expiration_period_days:
        teamSettings?.quote_expiration_period_days ?? null,
      address: teamSettings?.address ?? "",
      contact_email: teamSettings?.contact_email ?? "",
      contact_phone: teamSettings?.contact_phone ?? "",
      default_quote_note: teamSettings?.["shop.default_quote_note"] ?? "",
      default_email_body: teamSettings?.default_email_body ?? "",
      default_no_bid_email_body: teamSettings?.default_no_bid_email_body ?? "",
      default_terms_and_conditions:
        teamSettings?.["shop.default_terms_and_conditions"] ?? "",
      payment_terms: teamSettings?.["shop.payment_terms"] ?? "",
    });
  }, [teamSettings]);

  const { mutate: save, isPending } = useUpdateTeamSettings();
  const { uploadFiles, isUploading } = useUploadFiles({
    onSuccess: (fileIds) => {
      save({
        params: {
          path: {
            team_slug: teamSlug!,
          },
        },
        body: {
          logo_file_id: fileIds[0],
        },
      });
    },
  });

  return (
    <SettingsSectionLayout title="Quote Template">
      <form className="flex flex-col gap-8 text-sm items-start" ref={formRef}>
        <div className="flex flex-col gap-4">
          <div className="font-medium">Logo</div>
          <div className="flex items-center gap-4">
            <img
              className="rounded-full size-14"
              src={
                logo
                  ? URL.createObjectURL(logo)
                  : (teamSettings?.logo_url ?? "")
              }
            />
            <label className="underline text-blue-600 cursor-pointer">
              <input
                className="hidden"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setLogo(file); // for fast preview only
                    uploadFiles([file]);
                  }
                }}
              />
              Change Picture
            </label>
          </div>
        </div>
        <Input
          label="Address"
          value={input?.address ?? ""}
          onChange={(address) => setInput({ ...input!, address })}
          placeholder="Enter street address"
          width={inputWidth}
        />
        <Input
          label="Business Email"
          value={input?.contact_email ?? ""}
          onChange={(contact_email) => setInput({ ...input!, contact_email })}
          placeholder="Enter business email"
          width={inputWidth}
        />
        <Input
          label="Business Phone"
          value={input?.contact_phone ?? ""}
          onChange={(contact_phone) => setInput({ ...input!, contact_phone })}
          placeholder="Enter business phone"
          width={inputWidth}
        />
        <Input
          label="Default Quote Note"
          value={input?.default_quote_note ?? ""}
          onChange={(default_quote_note) =>
            setInput({ ...input!, default_quote_note })
          }
          placeholder="Enter default quote note"
          rows={3}
          width={inputWidth}
        />
        <Input
          label="Default Email Body"
          value={input?.default_email_body ?? ""}
          onChange={(default_email_body) =>
            setInput({ ...input!, default_email_body })
          }
          placeholder="Enter default email body"
          rows={5}
          width={inputWidth}
        />
        <Input
          label="Default No Bid Email Body"
          value={input?.default_no_bid_email_body ?? ""}
          onChange={(default_no_bid_email_body) =>
            setInput({ ...input!, default_no_bid_email_body })
          }
          placeholder="Enter default no bid email body"
          rows={5}
          width={inputWidth}
        />
        <Input
          label="Default Terms & Conditions (bottom of quote)"
          value={input?.default_terms_and_conditions ?? ""}
          onChange={(default_terms_and_conditions) =>
            setInput({ ...input!, default_terms_and_conditions })
          }
          placeholder="Enter default terms & conditions"
          rows={5}
          width={inputWidth}
        />
        <Input
          label="Payment Terms"
          value={input?.payment_terms ?? ""}
          onChange={(payment_terms) => setInput({ ...input!, payment_terms })}
          placeholder="Enter payment terms"
          width={inputWidth}
        />
        <Input
          label="Default Expiration"
          value={input?.quote_expiration_period_days?.toString() ?? ""}
          onChange={(value) =>
            setInput({
              ...input!,
              quote_expiration_period_days: parseInt(value) || null,
            })
          }
          width={inputWidth}
          type="number"
          required
        />
        <Button
          primary
          size="lg"
          loading={isPending || isUploading}
          onClick={(e) => {
            e.preventDefault();
            if (formRef.current?.checkValidity() === false) {
              formRef.current.reportValidity();
              return;
            }

            save({
              body: input!,
              params: {
                path: {
                  team_slug: teamSlug!,
                },
              },
            });
          }}
        >
          Save Changes
        </Button>
      </form>
    </SettingsSectionLayout>
  );
}

const inputWidth = 500;
